/**
 * @file apps/web/src/lib/useProjectRefresh.ts
 * @description "Reload me when the project changed under me" for pages that load
 * their data once on mount. Two triggers:
 *   - a gate input changed in this tab (notifyProjectOverviewChanged — fired by
 *     the Discovery, Design, Documents, Approvals and Build pages), and
 *   - the window regained focus / the tab became visible again — which is how a
 *     change made in ANOTHER window shows up (e.g. the client approves an FDD in
 *     the portal window, the consultant clicks back to this one). Supabase
 *     Realtime is not wired up, so this is the cross-window mechanism.
 *
 * attachProjectRefresh() holds the logic with its event sources injected, so it
 * is unit-tested without a DOM; useProjectRefresh() is the thin React wrapper.
 */

import { useEffect, useRef } from 'react';
import { subscribeProjectOverviewChanged } from './projectOverviewEvents.js';

export interface FocusSources {
  /** Subscribe to "the window regained focus" and "the tab visibility changed"; returns an unsubscribe. */
  onFocus:      (handler: () => void) => () => void;
  onVisibility: (handler: () => void) => () => void;
  isVisible:    () => boolean;
}

const browserSources: FocusSources = {
  onFocus: (handler) => { window.addEventListener('focus', handler); return () => window.removeEventListener('focus', handler); },
  onVisibility: (handler) => { document.addEventListener('visibilitychange', handler); return () => document.removeEventListener('visibilitychange', handler); },
  isVisible: () => document.visibilityState === 'visible',
};

/** Wires `refresh` to project-change notifications and window focus; returns a detach function. */
export function attachProjectRefresh(
  projectId: string,
  refresh: () => void,
  sources: FocusSources = browserSources,
): () => void {
  const offNotify = subscribeProjectOverviewChanged(projectId, refresh);
  const onVisible = () => { if (sources.isVisible()) refresh(); };
  const offFocus = sources.onFocus(onVisible);
  const offVisibility = sources.onVisibility(onVisible);
  return () => { offNotify(); offFocus(); offVisibility(); };
}

export function useProjectRefresh(projectId: string | undefined, refresh: () => void): void {
  // Always call the latest `refresh` without re-subscribing every render.
  const latest = useRef(refresh);
  latest.current = refresh;

  useEffect(() => {
    if (!projectId) return;
    return attachProjectRefresh(projectId, () => latest.current());
  }, [projectId]);
}
